/**
 * The Agents tab of a team. One clear page:
 *   - requests waiting for you (if any);
 *   - the agents the team has shared: who owns each, who can use it, who covers the tokens, with Use / Ask owner and a "⋯" menu for the actions;
 *   - sharing your own agents (who can use it, who covers the tokens), collapsed until you want it;
 *   - the Lyrans on the team;
 *   - usage: a summary and recent runs, a page at a time.
 *
 * Who may do what: anyone shares their own agents and asks for others'; an agent's owner changes how theirs is shared and answers requests;
 * the team owner can block or remove any agent and silence a Lyran.
 */
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Bot, Send, Share2 } from 'lucide-react';
import { spacesAPI } from '../../services/api.js';
import { T, btn, Pill, Section, Switch, RowMenu, kTokens } from './teamUi.jsx';
import MyAgentsShareList, { PAYER_NOTICE } from './MyAgentsShareList.jsx';
import TeamUsagePanel from './TeamUsagePanel.jsx';

const input = { flex: 1, minWidth: 0, height: 32, border: `1px solid ${T.border}`, borderRadius: 9, padding: '0 11px', fontSize: 12.5, background: T.surface, color: T.ink, fontFamily: 'inherit' };
const fail = (e, fallback) => toast.error(e?.response?.data?.message || fallback);

/** Who covers the tokens, in the viewer's words. */
const tokensNote = (a) => (a.payer === 'requester' ? 'Tokens: whoever uses it' : `Tokens: ${a.mine ? 'you' : a.ownerName}`);

export default function TeamAgentsPanel({ spaceId, meId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blocking, setBlocking] = useState(null);     // { id, kind: 'agent' | 'lyran', reason }
  const [requesting, setRequesting] = useState(null); // { id, prompt }
  const [sharing, setSharing] = useState(false);

  const load = useCallback(async () => {
    try { setData(await spacesAPI.agents(spaceId)); } catch { setData((d) => d || { agents: [], requests: [], lyrans: [], isOwner: false }); }
  }, [spaceId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    const onChange = () => load();
    window.addEventListener('lyra:roster-update', onChange);
    return () => { clearInterval(t); window.removeEventListener('lyra:roster-update', onChange); };
  }, [load]);

  const run = async (fn, ok, bad) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); window.dispatchEvent(new Event('lyra:roster-update')); }
    catch (e) { fail(e, bad); }
    finally { setBusy(false); }
  };

  if (!data) return <div style={{ padding: 24, color: T.ink3, fontSize: 13 }}>Loading agents…</div>;
  const { agents, requests, lyrans, isOwner } = data;
  const visible = agents.filter((a) => a.status !== 'removed');
  const myRequests = requests.filter((r) => String(r.ownerUserId) === String(meId));

  const sendRequest = async (a) => {
    const prompt = requesting.prompt.trim();
    let r;
    try { r = await spacesAPI.requestAgent(spaceId, a._id, prompt); }
    catch (e) {
      // Paid for by whoever uses it: confirm before any of your tokens are used.
      if (e?.response?.data?.needsConsent && window.confirm(`${e.response.data.message}\n\nContinue?`)) r = await spacesAPI.requestAgent(spaceId, a._id, prompt, true);
      else if (e?.response?.data?.needsConsent) return;
      else throw e;
    }
    setRequesting(null);
    toast.success(r.status === 'pending' ? 'Sent to the owner. You will be told when they answer.' : 'On it');
  };

  const menuFor = (a) => {
    const items = [];
    if (a.mine && a.status === 'active') {
      items.push({ label: a.access === 'open' ? 'Require my approval' : 'Open to everyone', hint: a.access === 'open' ? 'Teammates will ask you first' : 'Teammates can use it right away',
        onClick: () => run(() => spacesAPI.setAgentAccess(spaceId, a._id, a.access === 'open' ? 'ask' : 'open'), null, 'Could not change it') });
      items.push({ label: a.payer === 'requester' ? 'I cover the tokens' : 'Whoever uses it covers the tokens', disabled: a.payer !== 'requester' && a.private,
        hint: a.private && a.payer !== 'requester' ? 'It uses your private tools, so it always runs on your tokens' : a.payer === 'requester' ? 'Your keys pay for every run' : 'They confirm each run first',
        onClick: () => { if (a.payer !== 'requester' && !window.confirm(PAYER_NOTICE)) return; run(() => spacesAPI.setAgentPayer(spaceId, a._id, a.payer === 'requester' ? 'owner' : 'requester'), null, 'Could not change it'); } });
    }
    if (isOwner && a.status === 'active') items.push({ label: 'Block…', danger: true, hint: 'Stops it now, and halts any run in progress', onClick: () => setBlocking({ id: a._id, kind: 'agent', reason: '' }) });
    if (isOwner && a.status === 'blocked') items.push({ label: 'Unblock', onClick: () => run(() => spacesAPI.unblockAgent(spaceId, a._id), 'Unblocked', 'Could not unblock') });
    if ((isOwner || a.mine) && a.status !== 'removed') items.push({ label: a.mine ? 'Stop sharing' : 'Remove from team', danger: true,
      onClick: () => { if (window.confirm(a.mine ? `Stop sharing ${a.name}?` : `Remove ${a.name} from the team?`)) run(() => spacesAPI.removeAgent(spaceId, a._id), a.mine ? 'No longer shared' : 'Removed', 'Could not remove'); } });
    return items;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 900 }}>
      {myRequests.length > 0 && (
        <Section tone="accent" title={`Waiting for you (${myRequests.length})`} hint="Teammates asking to use your agents. An approval covers their conversation for 30 minutes.">
          {myRequests.map((r) => (
            <div key={r._id} style={{ padding: '11px 20px', borderTop: `1px solid ${T.accentMid}` }}>
              <div style={{ fontSize: 13, color: T.ink }}><b>{r.requesterName || 'A teammate'}</b> wants to use <b>{r.agentName}</b></div>
              <div style={{ fontSize: 12.5, color: T.ink3, margin: '2px 0 8px' }}>“{r.prompt}”</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={btn('primary')} disabled={busy} onClick={() => run(() => spacesAPI.approveAgentRequest(spaceId, r._id), 'Approved for 30 minutes', 'Could not approve')}>Approve for 30 minutes</button>
                <button style={btn()} disabled={busy} onClick={() => run(() => spacesAPI.approveAgentRequest(spaceId, r._id, true), 'Opened to the team', 'Could not approve')}>Always allow the team</button>
                <button style={btn()} disabled={busy} onClick={() => run(() => spacesAPI.declineAgentRequest(spaceId, r._id), 'Declined', 'Could not decline')}>Decline</button>
              </div>
            </div>
          ))}
        </Section>
      )}

      <Section
        title="Agents on this team"
        hint="Open agents can be used right away; the others ask their owner first. The owner decides who covers the tokens."
        actions={<button type="button" style={btn(sharing ? 'plain' : 'primary')} onClick={() => setSharing((v) => !v)}><Share2 size={14} /> {sharing ? 'Done sharing' : 'Share an agent'}</button>}
      >
        {sharing && (
          <div style={{ borderTop: `1px solid ${T.border}`, background: T.surface2 }}>
            <div style={{ padding: '12px 20px 2px', fontSize: 12.5, color: T.ink3 }}>
              Your agent runs with your own access, and the team owner can block it. You can also share from the conversation with the <b>Share</b> button under the message box.
            </div>
            <MyAgentsShareList spaceId={spaceId} onChanged={load} />
          </div>
        )}

        {visible.length === 0 && !sharing && (
          <div style={{ padding: '22px 20px 26px', borderTop: `1px solid ${T.border}`, textAlign: 'center', color: T.ink3, fontSize: 13 }}>
            Nothing shared yet.<br />
            <button type="button" style={{ ...btn('primary'), marginTop: 10 }} onClick={() => setSharing(true)}><Share2 size={14} /> Share one of your agents</button>
          </div>
        )}

        {visible.map((a) => {
          const items = menuFor(a);
          const open = a.access === 'open';
          const requestable = a.status === 'active' && !a.mine;
          return (
            <div key={a._id} style={{ padding: '14px 20px', borderTop: `1px solid ${T.border}`, opacity: a.status === 'blocked' ? 0.8 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ width: 34, height: 34, borderRadius: 10, background: T.accentLight, color: T.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Bot size={17} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 14, color: T.ink }}>{a.name}</b>
                    {a.status === 'blocked' && <Pill tone="bad">Blocked</Pill>}
                    {a.mine && <Pill>Yours</Pill>}
                  </div>
                  {a.description && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>{a.description}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {!a.mine && <Pill>by {a.ownerName}</Pill>}
                    <Pill tone={open ? 'accent' : 'plain'}>{open ? 'Open: use it now' : 'Ask first: owner approves'}</Pill>
                    <Pill title={a.payer === 'requester' ? 'You cover your own messages to it, after you confirm. Nobody else is charged for them.' : undefined}>{tokensNote(a)}</Pill>
                    {(data.covers || []).filter((c) => String(c.agentId) === String(a.agentId)).map((c) => <Pill key={c.agentId} tone="ok" title="Your cover for this conversation; it ends when it runs out or after 30 minutes">You cover this · {kTokens(c.tokensLeft)} left</Pill>)}
                  </div>
                  {a.status === 'blocked' && a.blockReason && <div style={{ fontSize: 12, color: T.danger, marginTop: 6 }}>Blocked: {a.blockReason}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                  {requestable && <button type="button" style={btn(open ? 'primary' : 'plain')} onClick={() => setRequesting(requesting?.id === a._id ? null : { id: a._id, prompt: '' })}>{open ? 'Use' : 'Ask owner'}</button>}
                  <RowMenu items={items} label={`Actions for ${a.name}`} />
                </div>
              </div>

              {blocking?.id === a._id && blocking.kind === 'agent' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <input autoFocus value={blocking.reason} onChange={(e) => setBlocking({ ...blocking, reason: e.target.value })} placeholder="Why? The team will see it (optional)" style={input} />
                  <button style={btn('danger')} disabled={busy} onClick={() => run(async () => { await spacesAPI.blockAgent(spaceId, a._id, blocking.reason); setBlocking(null); }, 'Blocked and halted', 'Could not block')}>Block now</button>
                  <button style={btn()} onClick={() => setBlocking(null)}>Cancel</button>
                </div>
              )}
              {requesting?.id === a._id && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <input autoFocus value={requesting.prompt} onChange={(e) => setRequesting({ ...requesting, prompt: e.target.value })}
                    placeholder={open ? `What should ${a.name} do?` : `What do you need ${a.name} for? ${a.ownerName} will see this`} style={input} />
                  <button style={btn('primary')} disabled={busy || !requesting.prompt.trim()} onClick={() => run(() => sendRequest(a), null, 'Could not send the request')}><Send size={13} /> {open ? 'Run' : 'Send request'}</button>
                </div>
              )}
            </div>
          );
        })}
      </Section>

      <Section title="Lyrans on this team" hint="Each member's Lyran can answer team messages when they fit. Turn yours off to have it speak only when someone @mentions it.">
        {lyrans.map((l) => {
          const me = String(l.userId) === String(meId);
          return (
            <div key={l.userId} style={{ borderTop: `1px solid ${T.border}`, padding: '11px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 28, height: 28, borderRadius: 8, background: T.surface3, color: T.ink2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{(l.lyranName || 'L')[0].toUpperCase()}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontSize: 13, color: T.ink }}>{l.lyranName || 'Lyran'}</b> {me && <Pill>Yours</Pill>}
                  <div style={{ fontSize: 12, color: l.lyranBlocked ? T.danger : T.ink3 }}>
                    {l.lyranBlocked ? `Blocked by the team owner${l.lyranBlockReason ? `: ${l.lyranBlockReason}` : ''}` : l.autoReply ? 'Replies on its own when a message fits' : 'Only when someone @mentions it'}
                  </div>
                </div>
                {me && !l.lyranBlocked && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.ink3 }}>
                    Reply on its own <Switch on={l.autoReply} disabled={busy} label="Reply on its own" onChange={(v) => run(() => spacesAPI.setAutoReply(spaceId, v), null, 'Could not save')} />
                  </label>
                )}
                {isOwner && !me && !l.lyranBlocked && <button style={btn('danger')} onClick={() => setBlocking({ id: l.userId, kind: 'lyran', reason: '' })}>Block…</button>}
                {isOwner && !me && l.lyranBlocked && <button style={btn()} disabled={busy} onClick={() => run(() => spacesAPI.unblockLyran(spaceId, l.userId), 'Unblocked', 'Could not unblock')}>Unblock</button>}
              </div>
              {blocking?.id === l.userId && blocking.kind === 'lyran' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <input autoFocus value={blocking.reason} onChange={(e) => setBlocking({ ...blocking, reason: e.target.value })} placeholder="Why? (optional)" style={input} />
                  <button style={btn('danger')} disabled={busy} onClick={() => run(async () => { await spacesAPI.blockLyran(spaceId, l.userId, blocking.reason); setBlocking(null); }, 'Blocked', 'Could not block')}>Block now</button>
                  <button style={btn()} onClick={() => setBlocking(null)}>Cancel</button>
                </div>
              )}
            </div>
          );
        })}
        <div style={{ height: 4 }} />
      </Section>

      <TeamUsagePanel spaceId={spaceId} />
    </div>
  );
}
